import React, { useRef, useState } from "react";

/* Display or edit "structured text", which can be either a regular text
  string or an array of "structured text items". See the server-side library
  ActivityData::Util::StructuredText for full details.

  A structured text item in regular form can be either a simple text string or
  an object with entries "type", "attributes", "subitems", and/or
  type-specific entries. It can also be represented in shorthand form as an
  array

  [ type, attributes (may be omitted), ...type-specific-strings, ...subitems ]
        
  (where, to avoid being mistaken for an attributes object, subitems must
   also be in shorthand form, so they are all either strings or arrays).

  The "type" attribute is always present. It can be one of the special type
  names below, designating an item that we render in a specific custom manner,
  or it can be an HTML element name in all lowercase.

  If present, "attributes" is an object with string-valued attribute entries.
  Some of our custom types take care of populating attributes (e.g., our
  "link" type automatically puts in href/target/rel) so only additional
  attributes need to be specified here.

  Special type names currently supported in the front end (some additional
  ones are implemented in the back-end receipt-rendering code but aren't
  yet needed here):

  (1) type="link". Has one type-specific entry "url". Generates an <a href=...
  element. Uses the url for the display text as well as the url if no
  subitems are present. If in "presentation" (non-editing) mode, suppresses the
  http(s):// part of the url when using it for display text.

  (2) type="email". One type-specific entry "address". Like type=link, but
  uses mailto:url as the href and address as the default display text.

  More to be added.

*/

import { Form } from "react-bulma-components";
const { Field, Control, Checkbox } = Form;

// Convert from shorthand form to full form (if passed an item that is
// already in full form, it will be left unchanged):

const typeSpecificEntries = { link: ["url"], email: ["address"] };
export function fullFromShorthand(item) {
  if (!Array.isArray(item)) return item;
  const [type, ...subitems] = item;
  const attributes =
    subitems.length > 0 &&
    typeof subitems[0] === "object" &&
    !Array.isArray(subitems[0])
      ? subitems.shift()
      : {};
  const fullItem = { type, attributes, subitems };
  typeSpecificEntries[type]?.forEach(
    (entryName) => (fullItem[entryName] = subitems.shift())
  );

  fullItem.subitems = fullItem.subitems?.map((i) => fullFromShorthand(i));
  return fullItem;
}

// And vice versa
export function shorthandFromFull(item) {
  if (item === null) return item;
  if (Array.isArray(item)) return item;
  if (typeof item !== "object") return item;
  const { type, attributes = {}, subitems = [] } = item;
  const shorthandItem = [type];
  if (Object.keys(attributes).length > 0) shorthandItem.push(attributes);
  typeSpecificEntries[type]?.forEach((entryName) => {
    if (item[entryName] !== "undefined") shorthandItem.push(item[entryName]);
  });
  shorthandItem.push(...subitems.map((subitem) => shorthandFromFull(subitem)));
  return shorthandItem;
}

// Do custom processing for our custom types, returning a generic-HTML-element
// structured text item.

const linkAttributes = (url) => {
  return { href: url, rel: "noopener", target: "_blank" };
};

const handleCustomType = {
  link: (item, editingMode) => {
    const { url, subitems = [], attributes = {} } = item;
    return {
      type: "a",
      subitems:
        subitems?.length > 0
          ? subitems
          : editingMode
          ? [url]
          : [url.replace(/^https?:\/\//, "")],
      attributes: { ...attributes, ...linkAttributes(url) },
    };
  },
  email: (item, editingMode) => {
    const { address, subitems = [], attributes = {} } = item;
    return {
      type: "a",
      subitems: subitems?.length > 0 ? item.subitems : [address],
      attributes: { ...attributes, ...linkAttributes("mailto:" + address) },
    };
  },
};

// If a generic item type that is read back from the DOM after editing is in
// the form of one of our custom types, return that custom type.

const recognizeCustomType = {
  a: (item) => {
    const { attributes, subitems } = item;
    const { href, target, rel, ...otherAttributes } = attributes;
    const singleDisplay = subitems?.length === 1 ? subitems[0] : null;
    if (target === "_blank" && rel === "noopener") {
      const emailMatch = href?.match(/^mailto:(.*)$/);
      if (emailMatch) {
        const [, address] = emailMatch;
        return {
          type: "email",
          address,
          attributes: otherAttributes,
          subitems: singleDisplay === address ? [] : subitems,
        };
      }
      const webMatch = href?.match(/^https?:\/\//);
      if (webMatch) {
        return {
          type: "link",
          url: href,
          attributes: otherAttributes,
          subitems: singleDisplay === href ? [] : subitems,
        };
      }
    }

    return item;
  },
};

// create - as a react component, unless in editing mode, in which case return
// a newly created DOM node -- the rendered form of a structured text item.

function renderItem(origItem, key, editingMode) {
  const fullItem = fullFromShorthand(origItem);
  if (typeof fullItem === "string") {
    if (editingMode) return document.createTextNode(fullItem);
    else return fullItem;
  }
  const handler = handleCustomType[fullItem.type];
  const genericItem = handler ? handler(fullItem, editingMode) : fullItem;
  if (editingMode) {
    const node = document.createElement(genericItem.type.toUpperCase());
    Object.entries(genericItem.attributes ?? {}).forEach(([attr, value]) =>
      node.setAttribute(attr, value)
    );
    genericItem.subitems?.forEach((item, i) =>
      node.appendChild(renderItem(item, i, true))
    );
    return node;
  } else {
    const Tag = genericItem.type;

    // Annoying ... can't use "class"; even though the actual use/deletion of
    // it works fine in non-strict mode, a TypeError is thrown in strict mode.
    const attributes = Object.fromEntries(
      Object.entries(genericItem.attributes ?? {}).map(([k, v]) => [
        k === "class" ? "className" : k,
        v,
      ])
    );

    const className = attributes.className;
    delete attributes.className;
    if (genericItem.subitems?.length > 0)
      return (
        <Tag key={key} className={className} {...attributes}>
          {genericItem.subitems.map((item, i) => renderItem(item, i, false))}
        </Tag>
      );
    else return <Tag key={key} className={className} attributes={attributes} />;
  }
}

export function StructuredText({ data, inline }) {
  const Wrapper = inline ? "span" : "div";
  if (Array.isArray(data))
    return (
      <Wrapper className="structured-text">
        {data.map((item, i) => renderItem(item, i, false))}
      </Wrapper>
    );
  else return renderItem(data, 0);
}

// EDITING STRUCTURED TEXT
// =======================

// This is accomplished by first rendering the text in edit mode, so that,
// instead of React-managed components, we insert raw DOM nodes into a
// contenteditable container, and leverage the browser's own capabilities
// for allowing the user to edit the text.

// Upon every change by the user, we do three things:

// (1) Validate the new node subtree, by removing any attributes or classes we
// don't want to allow, and removing altogether any elements of a type we
// don't want to allow (replacing such elements with their child nodes and
// renormalizing so that adjacent text nodes are merged into one).

// (2) Perform any automatic linking (looking for URL patterns in text nodes
// and replacing them with the corresponding <a> elements)

// (3) Reading the DOM node structure back and constructing the new
// structured text object from it.

// Steps (1) and (2) are performed by a "processEditedItem" function, and
// step (3) by

// Step 1: Validation of the new node subtree
// ==========================================

// More explanation: The user might be able to paste in extra formatting that
// we don't want. Also, the browser itself might insert extra formatting: for
// example, chrome likes to insert span tags to copy in chrome's default user
// stylesheet.

// We eliminate such elements, so that something like

//    This is <span style="whatever">a <b>chrome</b> text fragment</span>

// would become

//    This is a <b>chrome</b> text fragment

// Here, the span had three children: the text node "a ", the <b> element,
// and the text node " text fragment". These three nodes now get moved up
// to the parent's list of children, immediately following its first child,
// the "This is " text node. Renormalization then merges the two adjacent
// text nodes "This is " and "a " into a single text node "This is a ".

// We also at this stage remove any "automatic link" tags when, as a result
// of user editing, the href attribute no longer matches the contents (or
// there's now adjacent text beside it that ought to be incorporated). This
// would convert something like

//   Please refer to <a href="https://host/pat">https://host/pat</a>h

// (caused by the user adding an "h" at the end outside the link) back into
// the regular text

//  Please refer to https://host/path

// where it can be properly re-linked by the auto-linking process in step (2).

// The code for this step (1) is organized as follows:

// (1A) The validateChildNode function, return boolean

//       validateChildNode(element, child, allowed, seen)

// This function validates a specific child node "child" of a specific element
// node "element". It strips any undesired attributes and classes (as
// specified in allowed.tags and allowed.classes) and returns true if the
// stripped element is valid and can remain, but it returns false if the
// element needs to be removed. The "seen" argument is an array of element
// names of its ancestors, and is used to, for example, invalidate an "a" node
// that's already inside another "a" node (and also, in step B below, this
// prevents autolinking of text that's already inside an autolinked "a" node).

// (1B) The replaceChildElementWithItsChildren function (no return value)

//       replaceChildElementWithItsChildren(element, child)

// This replaces node "child", which is in parent "element", with its own
// child nodes instead.

// (1C) The removeInvalidChildren function, returns boolean

//          removeInvalidChildren(element, allowed, seen)

// This function calls validateChildNode on all of "element"'s children
// and calls replaceChildElementWithItsChildren on any where it got a false
// return value. It returns a boolean value indicating whether or not any
// children were removed, and also calls element.normalize() before returning
// true.

// Its calling function, processEditItem, will call removeInvalidChildren
// repeatedly until it returns false.

// REGEXPS
// =======

// Because the regexps for detecting text for auto-linking need to be used
// here (validating previously-made auto-links when text changes) as well
// as when auto-linking, we define them here. All need to be created with the
// "s" flag so that embedded newline characters are treated just like any
// other character and don't terminate the match-checking prematurely.

// Recognize text as a link only if it is at the beginning of the node or
// follows a space, parenthesis, or bracket. Thus, for example, the following
// links beginning with https:// will be recognized:

//   https://
//   previous stuff https://
//   previous stuff (https://

// and the following user@domain texts will be recognized as e-mail addresses:

//   user@domain
//   previous stuff user@domain
//   previous stuff (user@domain

// but something like abchttps:// will not be recognized as a web link
// and if someone removed the space from "some user@domain" making it
// "someuser@domain" the "user@domain" link would become invalid and
// be unlinked, allowing the full someuser@domain to be recognized.

// An initial ^.*? is included so this RegExp will return as its first
// capturing group the entirety of the text prior to first match, which is
// convenient when splitting text into text / link / text pieces. There is
// also a second capturing group, whose return data is not needed.

// RegExp definition string for before-link testing:
const REDefBeforeLink = "^(.*?(^|[\\s()\\[\\]<>]))";

// RegExp for checking the end of a text node that immediately precedes
// a link, to ensure the link is still appropriately matched:
const RegExpNodeBeforeLink = new RegExp(REDefBeforeLink + "$", "s");

// Links will be terminated by either the end of the node or by whitespace,
// optionally preceded by sentence- or phrase-terminating punctuation
// characters (period, comma, colon, semicolon, or exclamation, question or
// quotation mark, closing bracket or parenthesis), so that in text like

//     Please go to  "https://host/path."

// the ending ." will not be considered part of the link URL.

// A trailing .*$ allows all of the post-link text to be returned in the
// first capturing group of this RegExp. Again, there are two capturing
// groups, and the return value of the second is not needed.

const REDefAfterLink = "([.,;:?!'\")\\]>]*(\\s.*)?$)";
const RegExpNodeAfterLink = new RegExp("^" + REDefAfterLink, "s");

// RegExp definitions for matching the link iteself
const REDefWebLink = "(https?)://.*?";
const REDefEmailLink = "[-\\w.]+@[-\\w]+\\.[-\\w].*?";

// REDefWebLink has one capturing group (returning the protocol string);
// REDefEmailLink has none. Thus, this combined RegExp has two capturing
// groups, and the match is a web link if and only if the second group returns
// a non-null protocol string.
const REDefLink = "(" + REDefWebLink + "|" + REDefEmailLink + ")";

const RegExpCheckLink = new RegExp(
  REDefBeforeLink + REDefLink + REDefAfterLink,
  "s"
);

// PROTECTING THE SELECTION

// The trickiest part of steps 1 and 2 (validating, normalizing, and
// automatically linking) is maintaining the user's current selection and
// cursor position while doing any replacements. In addition, we also have to
// take care even when normalizing elements and not doing any actual
// replacements, because not all browsers correctly preserve the selection
// when merging adjacent text elements (though Chrome and Firefox do). We
// therefore have the following set of helper routines to:

// (a) Record the selection position relative to a stable ancestor element
// whose overall textContent will be unaffected by the manipulations we do,
// noting anchor and focus point positions relative to that overall textContent
// string;

// (b) After performing the manipulations, recalculate the selection positions
// relative to the same stable element. If there has been a change (meaning
// that the browser did not compensate appropriately), find the appropriate
// text node(s) relative to the updated node tree within that stable element,
// and reset the selection points to those nodes. If we don't do this, the
// cursor will appear to "jump" to a different position following the
// manipulations.

// Part (a), recording the selection position, is broken down into the following
// simpler routines:

// General utility to list all of an element's descendant elements (together
// with the element itself). We use this when we are going to remove an
// element, as then neither it nor its subelements will be stable reference
// elements for recording selection position.

function subElements(node) {
  return node.nodeType === Node.ELEMENT_NODE
    ? [node, Array.from(node.childNodes).map(subElements)].flat(2)
    : [];
}

// Helper to find a child element's character offset within the textContent
// of its parent element.

function childOffsetInParent(child, parent) {
  let seen = false;
  let offset = 0;
  parent?.childNodes?.forEach((sibling) => {
    if (sibling === child) seen = true;
    if (!seen) offset += sibling.textContent.length;
  });
  return offset;
}

// For either the anchor point or focus point of selection, find the closest
// stable containing element (skipping any subelement of the specified
// bypassElement, if any, that is due to be removed) and the offset of where
// the point is within that stable element's textContent.

function recordPosition(origNode, origOffset, bypassElement = null) {
  if (!origNode) return null;
  const bypassElements = bypassElement ? subElements(bypassElement) : [];
  let [element, offset] =
    origNode.nodeType === Node.TEXT_NODE
      ? [
          origNode.parentNode,
          childOffsetInParent(origNode, origNode.parentNode) + origOffset,
        ]
      : [
          origNode,
          childOffsetInParent(origNode.childNodes[origOffset], origNode),
        ];

  while (bypassElements.includes(element)) {
    [element, offset] = [
      element.parentNode,
      childOffsetInParent(element, element.parentNode) + offset,
    ];
  }
  return [element, offset];
}

// Part (b), resetting the selection position, is broken down into the following
// helpers:

// See if the newly calculated position has changed (does nothing if there
// was no old selection point).

function changedPosition(oldPosition, newPosition) {
  return oldPosition
    ? oldPosition[0] !== newPosition[0] || oldPosition[1] !== newPosition[1]
    : false;
}

// Retrieve a flat sequential list of all child and descendant text nodes
// within an element:

function textSubNodes(node) {
  return node.nodeType === Node.TEXT_NODE
    ? [node]
    : node.nodeType === Node.ELEMENT_NODE
    ? Array.from(node.childNodes)
        .map((n) => textSubNodes(n))
        .flat()
    : [];
}

// From such a list of text nodes (that together represent the textContent
// of the ancestor element), find the one containing the specified offset.
// Returns a pair: the node, and the offset within the node).

function findNodeAtOffset(offset, textNodes) {
  let [tryNode, ...laterNodes] = textNodes;
  let remainingOffset = offset;
  while (
    remainingOffset > tryNode.textContent.length &&
    laterNodes.length > 0
  ) {
    remainingOffset -= tryNode.textContent.length;
    tryNode = laterNodes.shift();
  }
  if (remainingOffset > tryNode.textContent.length)
    remainingOffset = tryNode.textContent.length;

  return [tryNode, remainingOffset];
}

// And now, the routine to recreate a stored anchor or focus position, finding
// the appropriate text node and offset within that node:

function recreatePosition(savedPosition) {
  if (!savedPosition) return [null, null];
  const [element, offset] = savedPosition;
  const textNodes = textSubNodes(element);
  if (textNodes.length === 0) {
    if (offset > 0)
      console.log(
        `WARNING: non-zero offset ${offset} in empty node ${element.nodeName}`
      );
    return [element, 0];
  }
  const [node, nodeOffset] = findNodeAtOffset(offset, textNodes);
  return [node, nodeOffset];
}

// And now the main helper routine to record the selection point positions
// relative to appropriate stable elements, perform a manipulation that may
// affect the specified element and its descendants, then check the resulting
// positions and readjust if necessary.

function performActionAndRestoreSelection(action, element) {
  const selection = window.getSelection();
  const anchor = recordPosition(
    selection.anchorNode,
    selection.anchorOffset,
    element
  );
  const focus = recordPosition(
    selection.focusNode,
    selection.focusOffset,
    element
  );

  action();

  let { anchorNode, anchorOffset, focusNode, focusOffset } = selection;
  const newAnchor = recordPosition(anchorNode, anchorOffset) || [];
  const newFocus = recordPosition(focusNode, anchorOffset) || [];
  if (changedPosition(anchor, newAnchor) || changedPosition(focus, newFocus)) {
    [anchorNode, anchorOffset] = recreatePosition(anchor);
    [focusNode, focusOffset] = recreatePosition(focus);
    selection.setBaseAndExtent(
      anchorNode,
      anchorOffset,
      focusNode,
      focusOffset
    );
  }
}

// The functions implementing step 1 -- see (1A), (1B), and (1C) above

function validateChildNode(element, child, allowed, seen) {
  if (child.nodeType === Node.TEXT_NODE) return true;
  if (child.nodeType !== Node.ELEMENT_NODE) return false;
  const tag = child.nodeName.toLowerCase();

  if (!allowed.tags.includes(tag)) return false;

  child.removeAttribute("style");

  const removeClasses = Array.from(child.classList).filter(
    (c) => !allowed.classes.includes(c)
  );
  if (removeClasses.length > 0) child.classList.remove(...removeClasses);

  if (child.classList.length === 0) child.removeAttribute("class");
  const attributes = Object.fromEntries(
    Array.from(child.attributes).map((n) => [n.nodeName, n.nodeValue])
  );

  // Remove attribute-less span tags
  if (tag === "span" && Object.keys(attributes).length === 0) return false;

  // Remove attribute-less singleton div tags inside a containing element;
  // that is, <element ...><div>...</div>/element> just becomes
  // <element ...>...</element>

  if (
    tag === "div" &&
    Object.keys(attributes).length === 0 &&
    element.childNodes.length === 1
  )
    return false;

  if (tag === "a") {
    if (seen.includes("a")) return false;
    if (allowed.linkManual) return true;
    if (!allowed.linkAuto) return false;
    const href = attributes.href;
    if (!href) return false;
    const displayText = child.textContent;
    const expectHref = href.match(/^https?:\/\//)
      ? displayText
      : "mailto:" + displayText;
    if (href !== expectHref) return false;
    if (
      child.previousSibling &&
      child.previousSibling.nodeType === Node.TEXT_NODE &&
      !child.previousSibling.nodeValue.match(RegExpNodeBeforeLink)
    )
      return false;
    if (
      child.nextSibling &&
      child.nextSibling.nodeType === Node.TEXT_NODE &&
      child.nextSibling.nodeValue.length > 0 &&
      !child.nextSibling.nodeValue.match(RegExpNodeAfterLink)
    )
      return false;
    return true;
  } else {
    return true;
  }
}

function replaceChildElementWithItsChildren(element, child) {
  const replacementNodes = Array.from(child.childNodes);

  performActionAndRestoreSelection(
    () => child.replaceWith(...replacementNodes),
    child
  );
}

function removeInvalidChildren(element, allowed, seen) {
  const children = Array.from(element.childNodes);
  let modified = false;

  children.forEach((child) => {
    if (!validateChildNode(element, child, allowed, seen)) {
      modified = true;
      replaceChildElementWithItsChildren(element, child);
    }
  });
  if (modified) performActionAndRestoreSelection(() => element.normalize());
  return modified;
}

// Step 2: Automatic Linking
// =========================

// Helper function for splitting a text node up into a list of newly created
// replacement nodes, and resetting the selection afterwards if necessary:

function splitTextIntoElements(textNode, replacementNodes) {
  performActionAndRestoreSelection(() =>
    textNode.replaceWith(...replacementNodes)
  );
}

// And finally, the main function for step 2: auto-link a selection of text.

// When calling itself recursively to handle the remainder of a text item
// after already recognizing a link earlier, the needNodes argument is set,
// telling it to return a list of nodes even if no more links were recognized
// (i.e., just return a singleton list of the original text).

// When being invoked otherwise, needNodes will be undefined, and then it
// will simply return null if no links were recognized.

function autoLinkText(text, needNodes) {
  // Web links we recognize because we expect the person to type in the
  // full URL as that is what would be used in the display text as well.
  // For e-mail address, though, nobody will be typing mailto: in the
  // display text, so just recognize common e-mail address patterns, not
  // a full validation of anything that's a technically legal e-mail address.
  const match = text.match(RegExpCheckLink);
  if (!match) return needNodes ? [text] : null;
  const [, preMatch, , display, http, postMatch] = match;
  const replacementNodes = [];
  if (preMatch.length > 0) replacementNodes.push(preMatch);
  const linkNode = document.createElement("A");
  linkNode.setAttribute("href", http ? display : "mailto:" + display);
  linkNode.setAttribute("target", "_blank");
  linkNode.setAttribute("rel", "noopener");
  linkNode.appendChild(document.createTextNode(display));
  replacementNodes.push(linkNode);
  if (postMatch.length > 0)
    replacementNodes.push(...autoLinkText(postMatch, true));
  // replaceWith will handle text strings just fine, but we also need to adjust
  // selection position, for which it's easier to work with all text nodes
  // not mix of nodes and strings.
  return replacementNodes.map((n) =>
    typeof n === "string" ? document.createTextNode(n) : n
  );
}

// Coordination of steps (1) and (2)
// =================================

// The processEditedItem function takes care of steps 1 and 2 by:
//   * calling the removeInvalidChildren function repeatedly on the
//     main element until all children are validated,
//   * calling itself recursively on each child element, to validate
//     nodes further down the tree, and also auto-link any text nodes
//     remaining after validation,
//   * finish by autolinking text nodes at the top level.

function processEditedItem(element, allowed, previouslySeen = []) {
  const seen = [...previouslySeen, element.nodeName.toLowerCase()];
  while (removeInvalidChildren(element, allowed, seen)) {}

  Array.from(element.childNodes)
    .filter((node) => node.nodeType === Node.ELEMENT_NODE)
    .forEach((childElement) => processEditedItem(childElement, allowed, seen));

  Array.from(element.childNodes).forEach((node) => {
    if (
      node.nodeType === Node.TEXT_NODE &&
      allowed.linkAuto &&
      !seen.includes("a")
    ) {
      const replacementNodes = autoLinkText(node.nodeValue);
      if (replacementNodes) {
        splitTextIntoElements(node, replacementNodes);
      }
    }
  });
}

// Step 3: read back a structured text item from an edited DOM node
// ================================================================

function readBackItem(node) {
  if (!node) return null;
  if (node.nodeType === Node.TEXT_NODE)
    return node.nodeValue.replaceAll(/\s+/g, " ");
  const item = {
    type: node.nodeName.toLowerCase(),
    attributes: Object.fromEntries(
      Array.from(node.attributes).map((n) => [n.nodeName, n.nodeValue])
    ),
    subitems: Array.from(node.childNodes).map((n) => readBackItem(n)),
  };
  // Remove trailing <br> or empty <div>'s from the end. (We don't remove
  // them from the DOM node structure since the person may be about to put
  // something after them, but we don't consider them to be part of the
  // returned structured text until then.)

  // Also replace all sequences of whitespace with a single space character.
  // This removes confusing hidden white space that browsers might leave
  // behind, such as embedded newlines. (It also means we don't support
  // non-breaking space characters, but that's for the best when there's
  // no visual cue in the editing widget differentiating them from regular
  // space. If we desire to enable non-breaking space support, we need a
  // more sophisticated widget.)

  // Eventually: also remove empty elements of all types that don't render
  // anything when empty (e.g., everything except tags like br and img).

  while (
    item.subitems.length > 0 &&
    (item.subitems[item.subitems.length - 1].type === "br" ||
      item.subitems[item.subitems.length - 1].type === "div") &&
    item.subitems[item.subitems.length - 1].subitems.length === 0
  ) {
    item.subitems.pop();
  }
  const recognizer = recognizeCustomType[item.type];
  return recognizer ? recognizer(item) : item;
}
export const LINK_NONE = "none";
export const LINK_AUTO = "auto";
// Manually setting different display text from URL: to be implemented later
export const LINK_MANUAL = "manual";

export const BASIC_ALLOWED_TAGS = ["div", "span", "br", "a", "b", "i", "ul"];

export function EditStructuredText({
  structuredText,
  setStructuredText,
  inline,
  wrapperProps = {},
  allowedTags = BASIC_ALLOWED_TAGS,
  allowedClasses = [],
  allowedLinkModes = [LINK_AUTO, LINK_NONE],

  // If forceArrayReturn is set, always return an array of items even if text
  // is just a simple string
  forceArrayReturn = false,

  // if forceShorthandReturn is set, always return items in shorthand format
  // rather than full format.
  forceShorthandReturn = false,

  // if returnEmptyAsNull is set, return null (instead of empty string or
  // empty array) if the text is empty.
  returnNullIfEmpty = false,
}) {
  const items = Array.isArray(structuredText)
    ? structuredText
    : [structuredText];
  const ref = useRef();
  const [linkMode, setLinkMode] = useState(allowedLinkModes[0]);

  const canLinkNone = allowedLinkModes.includes(LINK_NONE);
  const canLinkAuto = allowedLinkModes.includes(LINK_AUTO);

  // To do: check that the initial structuredText satisfies the allowedTags
  // and allowedLinkModes specifications and warn if it doesn't.

  const Wrapper = inline ? "span" : "div";
  const doUpdate = (currentLinkMode = linkMode) => {
    const allowed = {
      tags: allowedTags,
      classes: allowedClasses,
      linkAuto: currentLinkMode === LINK_AUTO,
      linkManual: currentLinkMode === LINK_MANUAL,
    };

    performActionAndRestoreSelection(() => ref.current.normalize());
    processEditedItem(ref.current, allowed);
    const readBack = readBackItem(ref.current);

    if (
      readBack.subitems?.length === 1 &&
      typeof readBack.subitems[0] === "string" &&
      !forceArrayReturn
    ) {
      const returnString = readBack.subitems[0];
      setStructuredText(
        returnString === "" && returnNullIfEmpty ? null : returnString
      );
    } else {
      const returnItems = forceShorthandReturn
        ? readBack.subitems.map(shorthandFromFull)
        : readBack.subitems;
      setStructuredText(
        returnItems.length === 0 && returnNullIfEmpty ? null : returnItems
      );
    }
  };

  return (
    <div className="structured-text-editor">
      <Wrapper
        {...wrapperProps}
        className="structured-text"
        contentEditable
        onInput={(e) => doUpdate()}
        ref={(wrapperElement) => {
          if (wrapperElement && !ref.current) {
            ref.current = wrapperElement;
            items.forEach((item, i) =>
              wrapperElement.appendChild(renderItem(item, i, true))
            );
          }
        }}
      />
      {canLinkNone && canLinkAuto ? (
        <Field>
          <Control>
            <Checkbox
              checked={linkMode === LINK_AUTO}
              onChange={(e) => {
                const newLinkMode = e.target.checked ? LINK_AUTO : LINK_NONE;
                setLinkMode(newLinkMode);
                doUpdate(newLinkMode);
              }}
            >
              {" "}
              Automatically link web URLs and e-mail addresses
            </Checkbox>
          </Control>
        </Field>
      ) : null}
    </div>
  );
}

export default StructuredText;
